import Fuse from "fuse.js";
import { correct } from "../domain/foodMeta";
import type { FoodItem } from "../domain/types";

/**
 * The food tables, fetched and searched in memory. (ADR-025, ADR-037)
 *
 * `/foods.json` is the table the app opens with: INDB's dishes, the staples, FNDDS's Indian
 * dishes and the label rows, ~900 rows and ~40 KB gzipped. It is kept out of the bundle so it is
 * cached and revalidated on its own rather than re-downloaded with every app update, and the
 * service worker precaches it, so the first offline launch after an install still has it.
 *
 * `/foods-all.json` is the rest of USDA's generic tables, ~13,000 rows and ~450 KB gzipped — too
 * much to parse before the Food tab opens on a phone, and mostly foods nobody here logs. It is
 * fetched only when a search asks for it ("Search all foods"), once, and the service worker keeps
 * it from then on, so it works offline after the first use.
 */

const URL = "/foods.json";
const ALL_URL = "/foods-all.json";

/** What the wider table holds: FNDDS, Foundation Foods and SR Legacy. It is fetched only on
 *  demand, but it ships with the app and any of its rows can be logged, so the sources notice
 *  credits these whether or not this phone has fetched it yet. */
export const WIDER_SOURCES: readonly string[] = ["f", "o", "u"];

/** Below this many matches in the table the app opens with, the picker offers the wider one. */
export const SEARCH_ALL_BELOW = 8;

interface Loaded {
  foods: FoodItem[];
  index: Fuse<FoodItem>;
}

let main: Loaded | null = null;
let mainInflight: Promise<FoodItem[]> | null = null;
let wide: Loaded | null = null;
let wideInflight: Promise<FoodItem[]> | null = null;

export interface FoodTable {
  foods: FoodItem[];
  source: string;
}

async function fetchTable(url: string): Promise<Loaded> {
  // Ordinary caching, not `force-cache`: the service worker already caches these files and
  // replaces them when the app updates. Forcing the HTTP cache on top of that pinned whatever
  // copy a browser happened to have and a regenerated table never reached anyone.
  const r = await fetch(url);
  if (!r.ok) throw new Error(`food table unavailable (HTTP ${r.status})`);
  const body = (await r.json()) as { foods?: FoodItem[] };
  // Corrections applied here, not in the generated file — see domain/foodMeta.ts.
  const foods = correct(Array.isArray(body.foods) ? body.foods : []);
  if (!foods.length) throw new Error("food table is empty");
  const index = new Fuse(foods, {
    // Name and aliases, and a threshold loose enough for "dahl"/"dal" and "chapathi"/"chapati" —
    // Indian dish names have no single spelling, which is the whole reason for fuzzy search.
    keys: ["n", "a"],
    threshold: 0.38,
    ignoreLocation: true,
    minMatchCharLength: 2,
  });
  return { foods, index };
}

export async function loadFoods(): Promise<FoodItem[]> {
  if (main) return main.foods;
  if (mainInflight) return mainInflight;
  mainInflight = fetchTable(URL).then((t) => {
    main = t;
    return t.foods;
  });
  try {
    return await mainInflight;
  } finally {
    mainInflight = null;
  }
}

/** The wider table, fetched on the first "Search all foods" and kept for the session. */
export async function loadAllFoods(): Promise<FoodItem[]> {
  if (wide) return wide.foods;
  if (wideInflight) return wideInflight;
  wideInflight = fetchTable(ALL_URL).then((t) => {
    wide = t;
    return t.foods;
  });
  try {
    return await wideInflight;
  } finally {
    wideInflight = null;
  }
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** A word starting with `w`. Not `\b`: JavaScript's word boundary knows only ASCII letters, so it
 *  never fires inside Devanagari and "रोटी" as a second alias was unfindable. A letter, a
 *  combining mark (a matra) or a digit just before is what makes it the middle of a word. */
const wordStart = (w: string) => new RegExp(`(?<![\\p{L}\\p{M}\\p{N}])${escapeRe(w)}`, "u");

/** Lowercased and in one Unicode form, so a Hindi keyboard's decomposed nukta still matches. */
const norm = (s: string) => s.trim().toLowerCase().normalize("NFC");

/**
 * Ranked matches, in four tiers: names that start with the query, then names containing it as a
 * whole word, then — for a query of several words, "aloo gobi" — rows holding every one of them
 * in any order, then whatever fuzzy matching finds. Aliases (domain/foodMeta.ts: a row's own, and
 * the Hindi-script and Hinglish home words) count as part of the name.
 *
 * Fuse alone gets the common cases wrong. Typing "milk" ranked "Buttermilk biscuit" and
 * "Flavoured milkshake" above "Milk, cow (whole)" — a plain glass of milk was unreachable through
 * the one word anybody would type for it. Fuzzy matching earns its place on "chapathi", not here.
 *
 * Within a tier, an exact name comes first, then staples before dishes and shorter names before
 * longer: searching "dal" should offer plain boiled dal before "Dal dhokli". FNDDS's dishes rank
 * with INDB's — both are dishes, where the staples are plain foods. Between those, a row whose own
 * name holds the query leads one found only through an alias: "chawal" is an alias of every rice
 * dish, and "Boiled rice (Uble chawal)" should come before "Rice upma".
 */
export function orderMatches(foods: FoodItem[], q: string): FoodItem[] {
  const query = norm(q);
  if (query.length < 2) return [];

  const word = wordStart(query);
  const parts = query.split(/[\s,]+/).filter((w) => w.length >= 2);
  const every = parts.length > 1 ? parts.map(wordStart) : null;
  const starts: FoodItem[] = [];
  const contains: FoodItem[] = [];
  const all: FoodItem[] = [];
  const byName = new Set<FoodItem>();
  for (const f of foods) {
    const n = f.n.toLowerCase();
    // Aliases rank with the name: "wafers" is what potato chips are called.
    const a = (f.a || "").toLowerCase();
    const inName = n.startsWith(query) || word.test(n);
    if (n.startsWith(query) || a.startsWith(query)) starts.push(f);
    else if (inName || word.test(a)) contains.push(f);
    else if (every?.every((r) => r.test(n) || r.test(a))) all.push(f);
    else continue;
    if (inName) byName.add(f);
  }

  // An exact name first, the user's own food before a table row of the same name — they typed
  // it in because the table's did not suit. Then plain foods (staples, labels, their own) before
  // the dishes.
  const tier = (f: FoodItem) =>
    f.n.toLowerCase() === query ? (f.src === "c" ? 0 : 1) : f.src === "i" || f.src === "f" ? 3 : 2;
  const named = (f: FoodItem) => (byName.has(f) ? 0 : 1);
  const rank = (a: FoodItem, b: FoodItem) => tier(a) - tier(b) || named(a) - named(b) || a.n.length - b.n.length;
  starts.sort(rank);
  contains.sort(rank);
  all.sort(rank);
  return [...starts, ...contains, ...all];
}

function search(t: Loaded, q: string, limit: number, table?: FoodItem[]): FoodItem[] {
  const query = norm(q);
  if (query.length < 2) return [];
  const exact = orderMatches(table ?? t.foods, query);
  const seen = new Set(exact.map((f) => f.i));
  const fuzzy = t.index.search(query, { limit }).map((r) => r.item).filter((f) => !seen.has(f.i));
  return [...exact, ...fuzzy].slice(0, limit);
}

/**
 * `table` is what to search when it is more than the generated rows — the picker passes
 * `domain/customFoods.ts::foodTable`, so the user's own foods rank by the same rules. Fuzzy
 * matching stays on the generated rows' index; their own foods are few and named by them.
 */
export function searchFoods(q: string, limit = 25, table?: FoodItem[]): FoodItem[] {
  return main ? search(main, q, limit, table) : [];
}

/** Matches in the wider table only, or none while it has not been fetched. */
export function searchAllFoods(q: string, limit = 25): FoodItem[] {
  return wide ? search(wide, q, limit) : [];
}

/**
 * The picker's list once the wider table is in: every row of the table the app opens with first,
 * then the wider table's, so where both have a "Chapati or roti" the Indian row leads. A row is
 * never listed twice.
 */
export function mergeWider(first: FoodItem[], wider: FoodItem[], limit = 40): FoodItem[] {
  const seen = new Set(first.map((f) => f.i));
  return [...first, ...wider.filter((f) => !seen.has(f.i))].slice(0, Math.max(limit, first.length));
}

export function foodByCode(code: string): FoodItem | null {
  return main?.foods.find((f) => f.i === code) ?? wide?.foods.find((f) => f.i === code) ?? null;
}

/** True once the table is in memory, so a screen can tell "loading" from "no matches". */
export function foodsReady(): boolean {
  return main !== null;
}

/** True once the wider table has been fetched this session. */
export function allFoodsReady(): boolean {
  return wide !== null;
}

/** The `src` codes the app's food values come from — the table it opens with, plus the wider
 *  table's — which is what the About page's notice must credit. Empty until `loadFoods`. */
export function shippedSources(): Set<string> {
  const out = new Set<string>();
  if (!main) return out;
  for (const f of main.foods) if (f.src) out.add(f.src);
  for (const s of WIDER_SOURCES) out.add(s);
  return out;
}
